export const merryChatAddMenuViews = [
  "default",
  "tools",
  "capabilities",
  "library-categories",
  "library-assets",
  "video-parameters",
  "image-parameters",
  "extension-overflow",
] as const;

export type MerryChatAddMenuView = (typeof merryChatAddMenuViews)[number];

export interface MerryChatAddMenuSize {
  height: 312 | 396;
  width: 160 | 480 | 640;
}

const viewConfig = {
  default: { figmaNodeId: "434:44643", height: 312, width: 160 },
  tools: { figmaNodeId: "434:44641", height: 312, width: 480 },
  capabilities: { figmaNodeId: "434:44642", height: 312, width: 480 },
  "library-categories": {
    figmaNodeId: "434:45340",
    height: 312,
    width: 480,
  },
  "library-assets": {
    figmaNodeId: "438:80535",
    height: 312,
    width: 640,
  },
  "video-parameters": {
    figmaNodeId: "506:63874",
    height: 396,
    width: 640,
  },
  "image-parameters": {
    figmaNodeId: "506:64290",
    height: 396,
    width: 640,
  },
  "extension-overflow": {
    figmaNodeId: "572:53746",
    height: 312,
    width: 160,
  },
} as const satisfies Record<
  MerryChatAddMenuView,
  MerryChatAddMenuSize & { figmaNodeId: string }
>;

export function getMerryChatAddMenuFigmaNodeId(
  view: MerryChatAddMenuView,
) {
  return viewConfig[view].figmaNodeId;
}

export function getMerryChatAddMenuSize(
  view: MerryChatAddMenuView,
): MerryChatAddMenuSize {
  return viewConfig[view];
}

export const videoAspectRatioOptions = [
  { label: "1 : 1 方形", value: "1:1" },
  { label: "4 : 3 横向", value: "4:3" },
  { label: "3 : 4 竖向", value: "3:4" },
  { label: "16 : 9 宽屏", value: "16:9" },
  { label: "9 : 16 竖屏", value: "9:16" },
  { label: "21 : 9 超宽屏", value: "21:9" },
] as const;

export const imageAspectRatioOptions = [
  { label: "1 : 1 方形", value: "1:1" },
  { label: "3 : 2 横向", value: "3:2" },
  { label: "2 : 3 竖向", value: "2:3" },
  { label: "4 : 3 横向", value: "4:3" },
  { label: "3 : 4 竖向", value: "3:4" },
  { label: "16 : 9 宽屏", value: "16:9" },
  { label: "9 : 16 竖屏", value: "9:16" },
] as const;

export const videoResolutionOptions = ["768P", "2K"] as const;
export const videoDurationOptions = [5, 10, 15] as const;
export const videoSoundOptions = ["with-sound", "muted"] as const;
export const imageLongEdgeOptions = [1024, 1536, 2048] as const;

export type MerryVideoAspectRatio =
  (typeof videoAspectRatioOptions)[number]["value"];
export type MerryImageAspectRatio =
  (typeof imageAspectRatioOptions)[number]["value"];
export type MerryVideoResolution = (typeof videoResolutionOptions)[number];
export type MerryVideoDuration = (typeof videoDurationOptions)[number];
export type MerryVideoSound = (typeof videoSoundOptions)[number];
export type MerryImageLongEdge = (typeof imageLongEdgeOptions)[number];

export interface MerryVideoParameters {
  aspectRatio: MerryVideoAspectRatio;
  duration: MerryVideoDuration;
  resolution: MerryVideoResolution;
  sound: MerryVideoSound;
}

export interface MerryImageParameters {
  aspectRatio: MerryImageAspectRatio;
  longEdge: MerryImageLongEdge;
}

export const defaultMerryVideoParameters: MerryVideoParameters = {
  aspectRatio: "16:9",
  duration: 10,
  resolution: "768P",
  sound: "with-sound",
};

export const defaultMerryImageParameters: MerryImageParameters = {
  aspectRatio: "16:9",
  longEdge: 1536,
};

export function merryImageOutputDimensions(
  parameters: MerryImageParameters,
): { height: number; width: number } {
  const [widthRatio, heightRatio] = parameters.aspectRatio
    .split(":")
    .map(Number);
  const ratio = widthRatio! / heightRatio!;
  return ratio >= 1
    ? {
        height: Math.max(16, Math.round(parameters.longEdge / ratio)),
        width: parameters.longEdge,
      }
    : {
        height: parameters.longEdge,
        width: Math.max(16, Math.round(parameters.longEdge * ratio)),
      };
}
